import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import axios from 'axios';

// Poll every 2 minutes
const POLL_INTERVAL_MS = 2 * 60 * 1000;
// Minimum mismatch streak before showing banner (debounces transient blips).
const REQUIRED_MISMATCH_STREAK = 2;

// Compute a stable frontend build signature from the CRA asset manifest.
// This file is generated at build time and is IDENTICAL across all frontend
// replicas in a deployment — so it doesn't flap behind a load balancer the
// way per-pod backend boot timestamps do.
const fetchBuildSignature = async () => {
  const res = await axios.get(`/asset-manifest.json?t=${Date.now()}`, {
    headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
    timeout: 8000,
  });
  const files = res.data?.files || {};
  // main.js + main.css filenames embed content hashes — perfect signature.
  return `${files['main.js'] || ''}|${files['main.css'] || ''}`;
};

const VersionChecker = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const initialVersionRef = useRef(null);
  const mismatchStreakRef = useRef(0);

  useEffect(() => {
    let mounted = true;
    let intervalId = null;

    const checkVersion = async () => {
      try {
        const remoteVersion = await fetchBuildSignature();
        if (!remoteVersion || remoteVersion === '|' || !mounted) return;

        // First successful fetch — remember the version this tab loaded with.
        if (initialVersionRef.current === null) {
          initialVersionRef.current = remoteVersion;
          return;
        }

        if (remoteVersion !== initialVersionRef.current) {
          // Require a small consecutive streak so a single CDN edge with a
          // stale cached copy can't trigger a false-positive banner.
          mismatchStreakRef.current += 1;
          if (mismatchStreakRef.current >= REQUIRED_MISMATCH_STREAK) {
            setShowBanner(true);
          }
        } else {
          mismatchStreakRef.current = 0;
        }
      } catch {
        // Network / backend hiccup — silently ignore; we'll retry on next tick.
      }
    };

    // Initial check + poll
    checkVersion();
    intervalId = setInterval(checkVersion, POLL_INTERVAL_MS);

    // Also check when tab regains focus — most useful trigger after a deploy.
    const onVisible = () => {
      if (document.visibilityState === 'visible') checkVersion();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      mounted = false;
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  if (!showBanner || dismissed) return null;

  const handleRefresh = () => {
    // Hard reload to bypass cached JS bundles.
    window.location.reload();
  };

  return (
    <div
      data-testid="version-update-banner"
      className="fixed bottom-4 right-4 z-[9999] max-w-sm bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl shadow-2xl border border-blue-400/30 backdrop-blur"
    >
      <div className="flex items-start gap-3 p-4">
        <div className="shrink-0 mt-0.5 w-9 h-9 rounded-full bg-white/15 flex items-center justify-center">
          <RefreshCw size={18} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">A new version is available</p>
          <p className="text-xs text-white/85 mt-0.5">
            Refresh to get the latest improvements. Your work is saved.
          </p>
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={handleRefresh}
              data-testid="version-banner-refresh-btn"
              className="bg-white text-blue-700 hover:bg-blue-50 transition-colors font-semibold text-xs px-3 py-1.5 rounded-lg shadow-sm"
            >
              Refresh now
            </button>
            <button
              onClick={() => setDismissed(true)}
              data-testid="version-banner-dismiss-btn"
              className="text-white/80 hover:text-white font-medium text-xs px-2 py-1.5"
            >
              Later
            </button>
          </div>
        </div>
        <button
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          className="shrink-0 text-white/70 hover:text-white"
          data-testid="version-banner-close-btn"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export default VersionChecker;
